<template>
  <div class="project-container">
    <!-- Hero Section -->
    <header class="hero-section">
      <div class="hero-content">
        <div class="hero-badge">Game Jam</div>
        <h1 class="hero-title">Code Game Jam 2024</h1>
        <p class="hero-subtitle">Création d'un jeu multijoueur en 24h chrono</p>
        <div class="hero-meta">
          <span class="meta-item"><i class="mdi mdi-timer"></i> 24 heures</span>
          <span class="meta-item"><i class="mdi mdi-account-group"></i> Équipe de 5</span>
          <span class="meta-item"><i class="mdi mdi-gamepad-variant"></i> Multijoueur local</span>
        </div>
      </div>
      <div class="hero-image">
        <img :src="require('@/assets/image/CGJ2024-Affiche-8eme-edition.jpg')" alt="Code Game Jam 2024"/>
      </div>
    </header>

    <!-- Challenge Card -->
    <section class="card">
      <div class="card-header">
        <i class="mdi mdi-fire"></i>
        <h2>Le Défi</h2>
      </div>
      <div class="card-content">
        <p class="lead-text">
          Pendant plus de <strong>24 heures non-stop</strong>, notre équipe de cinq membres s'est engagée
          dans un challenge unique : créer un jeu vidéo complet de A à Z dans un temps limité. Ce
          marathon de développement intense demande créativité, rapidité d'exécution, et une
          collaboration parfaite entre tous les membres de l'équipe.
        </p>
        <p>
          Le défi ne se limite pas seulement à la programmation : il faut également concevoir le
          gameplay, créer les assets graphiques, composer la musique, et tester le jeu pour s'assurer
          qu'il soit jouable et amusant. C'est une expérience complète qui met à l'épreuve toutes les
          facettes du développement de jeux vidéo dans un contexte de pression temporelle extrême.
        </p>

        <div class="stats-grid">
          <div class="stat-item">
            <div class="stat-number">24h</div>
            <div class="stat-label">De développement</div>
          </div>
          <div class="stat-item">
            <div class="stat-number">5</div>
            <div class="stat-label">Développeurs</div>
          </div>
          <div class="stat-item">
            <div class="stat-number">1</div>
            <div class="stat-label">Jeu créé</div>
          </div>
        </div>

        <div class="tech-stack">
          <span class="tech-badge">Unity</span>
          <span class="tech-badge">C#</span>
          <span class="tech-badge">GitHub</span>
          <span class="tech-badge">Modélisation 3D</span>
        </div>
      </div>
    </section>

    <!-- Project Card -->
    <section class="card">
      <div class="card-header">
        <i class="mdi mdi-castle"></i>
        <h2>Le Jeu : Donjons Multijoueurs</h2>
      </div>
      <div class="card-content">
        <h3 class="section-subtitle">Concept et Gameplay</h3>
        <p>
          Notre jeu est une aventure multijoueur local basée sur l'exploration de donjons. Les joueurs
          doivent collaborer pour progresser à travers des niveaux remplis d'obstacles, d'énigmes et
          d'ennemis. Le concept met l'accent sur la coopération : chaque joueur a un rôle spécifique
          et doit travailler en synergie avec les autres pour réussir.
        </p>
        <p>
          Le gameplay intègre des mécaniques de plateforme, de résolution d'énigmes et de combat. Les
          niveaux ont été conçus pour encourager la communication entre joueurs et récompenser le travail
          d'équipe. L'ambiance des donjons, avec ses pièges et ses secrets, crée une expérience immersive
          qui maintient les joueurs engagés tout au long de l'aventure.
        </p>

        <div class="image-showcase">
          <img :src="require('@/assets/image/cgj-preview-jeu.png')" alt="Aperçu du jeu"/>
          <span class="image-caption">Capture d'écran du jeu final</span>
        </div>
      </div>
    </section>

    <!-- My Role Card -->
    <section class="card">
      <div class="card-header">
        <i class="mdi mdi-account-hard-hat"></i>
        <h2>Mon Rôle</h2>
      </div>
      <div class="card-content">
        <h3 class="section-subtitle">Level Design & Modélisation 3D</h3>
        <p>
          J'étais responsable de la conception visuelle et technique des niveaux, un rôle crucial pour
          l'expérience de jeu et l'immersion des joueurs. Mon travail consistait à créer des environnements
          3D à la fois esthétiquement plaisants et fonctionnellement cohérents avec le gameplay.
        </p>
        <p>
          Chaque niveau a été pensé pour offrir un défi progressif aux joueurs, avec des obstacles de
          plus en plus complexes et des énigmes qui nécessitent réflexion et coordination. J'ai dû
          jongler entre esthétique et performance, en optimisant les modèles 3D pour maintenir un
          framerate stable même avec plusieurs joueurs simultanés.
        </p>

        <div class="responsibility-list">
          <div class="responsibility-item">
            <div class="responsibility-icon">
              <i class="mdi mdi-cube-outline"></i>
            </div>
            <div class="responsibility-content">
              <h4>Modélisation 3D des Niveaux</h4>
              <p>Création des environnements 3D complets pour les donjons, incluant l'architecture des salles,
              les passages secrets, et les zones de boss. Optimisation des meshes pour maintenir de bonnes
              performances même avec plusieurs joueurs. Utilisation de techniques de level-of-detail (LOD)
              pour gérer efficacement les ressources.</p>
            </div>
          </div>

          <div class="responsibility-item">
            <div class="responsibility-icon">
              <i class="mdi mdi-puzzle"></i>
            </div>
            <div class="responsibility-content">
              <h4>Éléments Interactifs</h4>
              <p>Conception et implémentation des objets interactifs tels que les leviers, portes, pièges,
              plateformes mobiles et systèmes de téléportation. Création de mécaniques de jeu engageantes
              qui nécessitent la coopération entre joueurs pour être résolues. Intégration de feedbacks
              visuels et sonores pour améliorer la clarté des interactions.</p>
            </div>
          </div>

          <div class="responsibility-item">
            <div class="responsibility-icon">
              <i class="mdi mdi-palette"></i>
            </div>
            <div class="responsibility-content">
              <h4>Design Visuel</h4>
              <p>Développement de l'identité visuelle des environnements avec une attention particulière
              portée à l'éclairage et à l'atmosphère. Création d'une ambiance de donjon mystérieux grâce
              à l'utilisation stratégique de lumières, ombres et effets de particules. Harmonisation des
              couleurs et textures pour créer une expérience cohérente et immersive.</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Team Work Card -->
    <section class="card">
      <div class="card-header">
        <i class="mdi mdi-github"></i>
        <h2>Organisation d'Équipe</h2>
      </div>
      <div class="card-content">
        <p>
          La gestion du temps et la collaboration étaient essentielles dans ce challenge de 24 heures.
          Nous avons utilisé <strong>GitHub</strong> pour synchroniser notre code en temps réel et
          éviter les conflits. L'organisation a été clé pour notre réussite : chaque membre avait
          une spécialité définie, et nous communiquions constamment pour nous assurer que toutes
          les pièces du puzzle s'assemblaient correctement.
        </p>
        <p>
          Nous avons travaillé par sprints courts d'1-2 heures, avec des points de synchronisation
          réguliers pour s'assurer que tout le monde avançait dans la bonne direction. Cette méthode
          nous a permis de rester agiles et de nous adapter rapidement aux imprévus techniques ou
          aux changements de design nécessaires.
        </p>

        <div class="feature-grid">
          <div class="feature-item">
            <div class="feature-icon">
              <i class="mdi mdi-git"></i>
            </div>
            <h4>Version Control</h4>
            <p>Branches dédiées et merges réguliers</p>
          </div>

          <div class="feature-item">
            <div class="feature-icon">
              <i class="mdi mdi-message-text"></i>
            </div>
            <h4>Communication</h4>
            <p>Coordination constante via Discord</p>
          </div>

          <div class="feature-item">
            <div class="feature-icon">
              <i class="mdi mdi-calendar-check"></i>
            </div>
            <h4>Planning</h4>
            <p>Sprints courts et objectifs clairs</p>
          </div>

          <div class="feature-item">
            <div class="feature-icon">
              <i class="mdi mdi-account-multiple"></i>
            </div>
            <h4>Rôles Définis</h4>
            <p>Chacun avec sa spécialité</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Learning Card -->
    <section class="card learning-card">
      <div class="card-header">
        <i class="mdi mdi-school"></i>
        <h2>Apprentissages</h2>
      </div>
      <div class="card-content">
        <div class="learning-grid">
          <div class="learning-category">
            <h4>Gestion de Projet</h4>
            <ul>
              <li>Travail sous pression et deadlines serrées</li>
              <li>Priorisation des fonctionnalités (MVP)</li>
              <li>Coordination d'équipe en temps réel</li>
            </ul>
          </div>

          <div class="learning-category">
            <h4>Compétences Techniques</h4>
            <ul>
              <li>Modélisation 3D rapide et efficace</li>
              <li>Optimisation pour performances</li>
              <li>Git en environnement collaboratif</li>
            </ul>
          </div>

          <div class="learning-category">
            <h4>Game Design</h4>
            <ul>
              <li>Level design et flow de jeu</li>
              <li>Création d'environnements immersifs</li>
              <li>Balance gameplay vs esthétique</li>
            </ul>
          </div>

          <div class="learning-category">
            <h4>Soft Skills</h4>
            <ul>
              <li>Communication efficace</li>
              <li>Gestion du stress</li>
              <li>Esprit d'équipe</li>
            </ul>
          </div>
        </div>

        <div class="highlight-box">
          <i class="mdi mdi-trophy"></i>
          <p>
            Cette expérience intense m'a appris l'importance de la <strong>planification</strong>,
            de la <strong>collaboration</strong> et de savoir faire des <strong>compromis</strong>
            pour atteindre un objectif commun dans un temps limité.
          </p>
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import {Vue, Component} from 'vue-facing-decorator';

@Component({})
export default class CGJComponent extends Vue {
}
</script>

<style scoped>
.project-container {
  font-family: 'Inter', 'Segoe UI', sans-serif;
  line-height: 1.8;
  color: var(--text-color);
  background-color: var(--background-color);
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  padding-left: 100px;
}

/* Hero Section */
.hero-section {
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: 40px;
  margin-bottom: 60px;
  padding: 60px 40px;
  background: linear-gradient(135deg, var(--card-background) 0%, var(--dark-bg-secondary) 100%);
  border-radius: 24px;
  border: 1px solid var(--dark-border);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
}

.hero-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.hero-badge {
  display: inline-block;
  width: fit-content;
  padding: 8px 16px;
  background: rgba(255, 111, 76, 0.2);
  color: #ff6f4c;
  border-radius: 20px;
  font-size: 0.85em;
  font-weight: 600;
  margin-bottom: 16px;
  border: 1px solid #ff6f4c;
}

.hero-title {
  font-size: 3.5em;
  font-weight: 800;
  color: var(--h1-color);
  margin: 0 0 16px 0;
  background: linear-gradient(135deg, #ffffff 0%, var(--tertiary-color) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.hero-subtitle {
  font-size: 1.4em;
  color: var(--sub-color);
  margin-bottom: 24px;
  font-weight: 400;
}

.hero-meta {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-muted);
  font-size: 0.95em;
}

.meta-item i {
  color: var(--primary-color);
}

.hero-image {
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-image img {
  max-width: 100%;
  height: auto;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(255, 111, 76, 0.3);
}

/* Card Styles */
.card {
  background: var(--card-background);
  border-radius: 20px;
  padding: 40px;
  margin-bottom: 32px;
  border: 1px solid var(--dark-border);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
}

.card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 32px rgba(19, 124, 139, 0.2);
  border-color: var(--primary-color);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 28px;
  padding-bottom: 20px;
  border-bottom: 2px solid var(--dark-border);
}

.card-header i {
  font-size: 32px;
  color: var(--primary-color);
}

.card-header h2 {
  font-size: 2em;
  font-weight: 700;
  color: var(--h2-color);
  margin: 0;
}

.card-content {
  color: var(--text-color);
}

.lead-text {
  font-size: 1.1em;
  line-height: 1.8;
  margin-bottom: 24px;
}

.section-subtitle {
  font-size: 1.4em;
  color: var(--h2-color);
  margin: 0 0 16px 0;
  font-weight: 600;
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin: 32px 0;
}

.stat-item {
  text-align: center;
  padding: 24px;
  background: var(--dark-bg-secondary);
  border-radius: 16px;
  border: 1px solid var(--dark-border);
}

.stat-number {
  font-size: 3em;
  font-weight: 800;
  color: var(--primary-color);
  line-height: 1;
  margin-bottom: 8px;
}

.stat-label {
  font-size: 0.9em;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 1px;
}

/* Tech Stack */
.tech-stack {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 24px;
}

.tech-badge {
  padding: 10px 20px;
  background: rgba(19, 124, 139, 0.15);
  color: var(--primary-color);
  border-radius: 12px;
  font-size: 0.9em;
  font-weight: 600;
  border: 1px solid rgba(19, 124, 139, 0.3);
  transition: all 0.3s ease;
}

.tech-badge:hover {
  background: rgba(19, 124, 139, 0.25);
  transform: translateY(-2px);
}

/* Responsibility List */
.responsibility-list {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: 32px;
}

.responsibility-item {
  display: grid;
  grid-template-columns: 80px 1fr;
  gap: 20px;
  padding: 24px;
  background: var(--dark-bg-secondary);
  border-radius: 16px;
  border: 1px solid var(--dark-border);
  transition: all 0.3s ease;
}

.responsibility-item:hover {
  background: var(--dark-bg-card);
  border-color: var(--primary-color);
  transform: translateX(8px);
}

.responsibility-icon {
  width: 80px;
  height: 80px;
  background: rgba(19, 124, 139, 0.2);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.responsibility-icon i {
  font-size: 40px;
  color: var(--primary-color);
}

.responsibility-content h4 {
  font-size: 1.3em;
  color: var(--h2-color);
  margin: 0 0 8px 0;
  font-weight: 600;
}

.responsibility-content p {
  color: var(--text-muted);
  margin: 0;
  line-height: 1.6;
}

/* Feature Grid */
.feature-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin: 32px 0;
}

.feature-item {
  padding: 24px;
  background: var(--dark-bg-secondary);
  border-radius: 16px;
  border: 1px solid var(--dark-border);
  text-align: center;
  transition: all 0.3s ease;
}

.feature-item:hover {
  background: var(--dark-bg-card);
  border-color: var(--primary-color);
  transform: translateY(-4px);
}

.feature-icon {
  width: 50px;
  height: 50px;
  background: rgba(19, 124, 139, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
}

.feature-icon i {
  font-size: 24px;
  color: var(--primary-color);
}

.feature-item h4 {
  font-size: 1.1em;
  color: var(--h2-color);
  margin: 0 0 8px 0;
  font-weight: 600;
}

.feature-item p {
  color: var(--text-muted);
  margin: 0;
  font-size: 0.9em;
}

/* Image Showcase */
.image-showcase {
  margin: 32px 0;
  text-align: center;
}

.image-showcase img {
  max-width: 100%;
  height: auto;
  border-radius: 16px;
  border: 1px solid var(--dark-border);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
}

.image-caption {
  display: block;
  margin-top: 12px;
  color: var(--text-muted);
  font-size: 0.9em;
  font-style: italic;
}

/* Learning Grid */
.learning-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
}

.learning-category {
  padding: 24px;
  background: var(--dark-bg-secondary);
  border-radius: 16px;
  border: 1px solid var(--dark-border);
}

.learning-category h4 {
  font-size: 1.2em;
  color: var(--primary-color);
  margin: 0 0 16px 0;
  font-weight: 600;
}

.learning-category ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.learning-category li {
  padding: 8px 0 8px 24px;
  color: var(--text-color);
  position: relative;
  font-size: 0.95em;
}

.learning-category li::before {
  content: '→';
  position: absolute;
  left: 0;
  color: var(--primary-color);
  font-weight: bold;
}

/* Highlight Box */
.highlight-box {
  padding: 32px;
  background: linear-gradient(135deg, rgba(19, 124, 139, 0.1) 0%, rgba(19, 124, 139, 0.05) 100%);
  border-radius: 16px;
  border: 2px solid var(--primary-color);
  display: flex;
  gap: 20px;
  align-items: center;
}

.highlight-box i {
  font-size: 48px;
  color: var(--primary-color);
}

.highlight-box p {
  margin: 0;
  font-size: 1.1em;
  line-height: 1.8;
}

/* Responsive */
@media (max-width: 768px) {
  .project-container {
    padding-left: 20px;
  }

  .hero-section {
    grid-template-columns: 1fr;
  }

  .hero-title {
    font-size: 2.5em;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .responsibility-item {
    grid-template-columns: 1fr;
    text-align: center;
  }

  .responsibility-icon {
    margin: 0 auto;
  }
}
</style>
